import React from 'react';
import styles from './FacebookLogo.module.css';

// Real official Facebook brand asset — fetched from Meta's Brand Resource
// Center (https://www.meta.com/brand/resources/facebook/logo/, "Primary
// Logo": Facebook Blue #1877F2 circle mark, white "f", on a white/transparent
// background) and staged by the Workspace Coordinator at
// .claude/temp/product/design/grant-access/assets/ before this component was
// built (PRD grant-access-connector-branding, M2/M5; TRD R1.1/R1.2/D9/D10 —
// this replaces the pre-existing hand-drawn assets/icons/facebook-pages.svg,
// which is NOT a verified Brand Resource Center asset and is left untouched
// for its own existing internal-dialog use in MetaAuth.jsx). Source file:
// "Facebook Brand Asset Pack.zip" -> Logo/Primary Logo/Facebook_Logo_Primary.png
// (2084x2084 native). Rendered as an opaque asset reference (<img src>),
// never inlined as raw SVG markup and never touched with a CSS
// fill/filter/color override -- an <img> physically cannot be recolored by
// CSS, unlike an inlined <svg> (same rationale as GoogleAnalyticsLogo.jsx).
import facebookLogoAsset from '../../../assets/icons/facebook-f-logo.png';

// Published minimum render size for the Facebook "f" mark (PRD M3): never
// smaller than 16px wide. Below this floor, a one-time dev-console warning
// is logged (not a hard clamp), mirroring GoogleAnalyticsLogo.jsx's pattern.
const MIN_SIZE = 16;
let hasWarnedSize = false;

// Facebook's clear-space rule (PRD M3): maintain clear space around the mark
// equal to at least 1/4 of the logo's own width on all sides. Implemented as
// the component's own internal padding (relative to the default render size)
// rather than left for each call site to remember -- see FacebookLogo.module.css.

/**
 * FacebookIcon — the icon-only Facebook "f" mark (Facebook Blue circle,
 * white "f"), real asset, colors locked.
 *
 * @param {number}  size      - Render size in px, square (default 32,
 *                              matching the connector-tile grid's icon size).
 * @param {string}  href        - Optional URL to wrap the mark in a link.
 * @param {string}  className   - Optional extra class on the outer wrapper.
 * @param {object}  style       - Optional inline style overrides on the <img>.
 * @param {object}  wrapperStyle - Optional inline style overrides on the
 *                                 outer wrapper (e.g. to zero out the default
 *                                 clear-space padding when embedding inside a
 *                                 container that already provides its own
 *                                 padding, like the connector-tile grid's
 *                                 iconBtn -- same escape hatch this file's
 *                                 header comment describes for className).
 */
export const FacebookIcon = ({ size = 32, href, className, style, wrapperStyle }) => {
  if (size < MIN_SIZE && !hasWarnedSize && process.env.NODE_ENV !== 'production') {
    hasWarnedSize = true;
    // eslint-disable-next-line no-console
    console.warn(
      `FacebookIcon: rendered at ${size}px, below the published ${MIN_SIZE}px minimum (PRD grant-access-connector-branding, M3).`
    );
  }

  const mark = (
    <img
      src={facebookLogoAsset}
      alt="Facebook"
      width={size}
      height={size}
      style={style}
    />
  );

  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Facebook"
      className={[styles.markWrapper, className].filter(Boolean).join(' ')}
      style={wrapperStyle}
    >
      {mark}
    </a>
  ) : (
    <span className={[styles.markWrapper, className].filter(Boolean).join(' ')} style={wrapperStyle}>{mark}</span>
  );
};

// Default export mirrors the icon-only mark, same convention as
// GoogleAnalyticsLogo.jsx's default export.
const FacebookLogo = FacebookIcon;

export default FacebookLogo;
